import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  ShoppingCart as OrdersIcon,
  CheckCircle as UsedIcon,
  Cancel as DiscardedIcon,
  HourglassEmpty as UnusedIcon,
  Warning as WarningIcon,
  Visibility as ViewIcon,
  FileDownload as ExportIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useOrg } from '../../contexts/OrgContext';
import Breadcrumbs from '../../components/Breadcrumbs';
import { orderAPI, clientAPI, clientInventoryAPI } from '../../services/api';

const ClientAnalytics = () => {
  const orgContext = useOrg();
  const orgValue = typeof orgContext === 'object' ? orgContext.org : orgContext;
  const org = orgValue?.toUpperCase() || 'EPL'; // Default to EPL if no org
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Overall dashboard data (no client selected)
  const [overallOrders, setOverallOrders] = useState([]);
  const [overallStats, setOverallStats] = useState({
    totalClients: 0,
    pendingOrders: 0,
    activeOrders: 0,
    totalRevenue: 0,
    totalInventoryTracked: 0
  });
  
  // Client inventory data
  const [clientInventory, setClientInventory] = useState([]);
  const [inventoryStats, setInventoryStats] = useState({
    total: 0,
    unused: 0,
    used: 0,
    discarded: 0,
    expiringWithin30Days: 0
  });
  
  // Client orders data
  const [clientOrders, setClientOrders] = useState([]);
  const [orderStats, setOrderStats] = useState({
    total: 0,
    totalValue: 0,
    avgOrderValue: 0,
    lastOrderDate: null
  });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);

  // Sort clients by most recent activity (last order date)
  const sortedClients = [...clients].sort((a, b) => {
    const aDate = a.lastOrderDate || a.createdAt || 0;
    const bDate = b.lastOrderDate || b.createdAt || 0;
    return new Date(bDate) - new Date(aDate);
  });

  useEffect(() => {
    loadClients();
    loadOverallData();
  }, [org]);

  useEffect(() => {
    const clientIdFromUrl = searchParams.get('clientId');
    if (clientIdFromUrl && clients.length > 0) {
      const client = clients.find(c => c.id === clientIdFromUrl);
      if (client) {
        setSelectedClient(client);
        loadClientData(client);
      }
    }
  }, [searchParams, clients]);

  useEffect(() => {
    if (selectedClient) {
      loadClientData(selectedClient);
    }
  }, [selectedClient, activeTab]);

  const loadClients = async () => {
    try {
      setLoading(true);
      console.log('Loading clients for org:', org);
      
      // Try first with specific org filter
      let url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api'}/users?userType=CLIENT&organization=${org}`;
      
      let response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        credentials: 'include'
      });
      
      let data = await response.json();
      
      // If no clients found with specific org, try fetching all clients (if admin)
      if (data.success && (!data.data || data.data.length === 0)) {
        console.log('No clients found for specific org, trying broader search...');
        url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api'}/users?userType=CLIENT`;
        
        response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          credentials: 'include'
        });
        
        data = await response.json();
      }
      
      console.log('Clients API response:', data);
      
      if (data.success) {
        const clientsList = data.data || [];
        console.log('Loaded clients:', clientsList.length);
        setClients(clientsList);
        setOverallStats(prev => ({ ...prev, totalClients: clientsList.length }));
      } else {
        console.error('Failed to load clients:', data.error);
        setError('Failed to load clients: ' + (data.message || data.error));
      }
    } catch (err) {
      console.error('Error loading clients:', err);
      setError(err.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const loadOverallData = async () => {
    try {
      // Load recent orders across all clients
      const ordersResponse = await orderAPI.getOrders({
        organization: org,
        limit: 20,
        page: 1
      });
      
      console.log('Overall orders response:', ordersResponse);
      
      if (ordersResponse.success) {
        const orders = ordersResponse.data || [];
        setOverallOrders(orders);
        
        // Calculate stats
        const pending = orders.filter(o => o.status === 'PENDING' || o.status === 'CONFIRMED').length;
        const active = orders.filter(o => ['PROCESSING', 'PICKING', 'PACKING'].includes(o.status)).length;
        const revenue = orders.reduce((sum, o) => sum + Number(o.totalAmount || 0), 0);
        
        setOverallStats(prev => ({
          ...prev,
          pendingOrders: pending,
          activeOrders: active,
          totalRevenue: revenue
        }));
      }

      // Load overall inventory tracking stats
      const inventoryResponse = await clientInventoryAPI.admin.getStats();
      if (inventoryResponse.success) {
        setOverallStats(prev => ({
          ...prev,
          totalInventoryTracked: inventoryResponse.data.total || 0
        }));
      }
    } catch (err) {
      console.error('Error loading overall data:', err);
    }
  };

  const loadClientData = async (client) => {
    if (!client) return;
    
    try {
      setLoading(true);
      
      if (activeTab === 0) {
        // Load overview data (orders and inventory summary)
        await Promise.all([
          loadClientOrders(client.id),
          loadClientInventorySummary(client.id)
        ]);
      } else if (activeTab === 1) {
        // Load detailed inventory
        await loadClientInventory(client.id);
      } else if (activeTab === 2) {
        // Load orders
        await loadClientOrders(client.id);
      }
    } catch (err) {
      console.error('Error loading client data:', err);
      setError(err.message || 'Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const loadClientOrders = async (clientId) => {
    try {
      const response = await orderAPI.getOrders({
        userId: clientId,
        organization: org,
        limit: 100
      });
      
      if (response.success) {
        const orders = response.data || [];
        setClientOrders(orders);
        
        // Calculate stats
        const totalValue = orders.reduce((sum, order) => sum + Number(order.totalAmount || 0), 0);
        const avgValue = orders.length > 0 ? totalValue / orders.length : 0;
        const lastOrder = orders.length > 0 ? orders[0] : null;
        
        setOrderStats({
          total: orders.length,
          totalValue,
          avgOrderValue: avgValue,
          lastOrderDate: lastOrder?.orderDate
        });
      }
    } catch (err) {
      console.error('Error loading client orders:', err);
    }
  };

  const loadClientInventorySummary = async (clientId) => {
    try {
      // Use the actual admin stats API
      const response = await clientInventoryAPI.admin.getStats(clientId);
      
      if (response.success) {
        setInventoryStats(response.data);
      }
    } catch (err) {
      console.error('Error loading inventory summary:', err);
    }
  };

  const loadClientInventory = async (clientId) => {
    try {
      // Use the actual admin inventory API
      const response = await clientInventoryAPI.admin.getAllInventory({
        userId: clientId,
        status: statusFilter,
        page: page + 1,
        limit: rowsPerPage
      });
      
      if (response.success) {
        setClientInventory(response.data);
      }
    } catch (err) {
      console.error('Error loading client inventory:', err);
    }
  };

  const handleClientSelect = (event, client) => {
    // Handle both Autocomplete event (where client is 2nd arg) 
    // and direct button click (where client might be 1st or 2nd arg depending on how it's called)
    const selected = client || event;
    
    if (selected && selected.id) {
      console.log('Selecting client:', selected);
      setSelectedClient(selected);
      setSearchParams({ clientId: selected.id });
      setActiveTab(0);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleViewDetails = (item) => {
    setSelectedInventoryItem(item);
    setDetailsDialogOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'UNUSED': return 'success';
      case 'USED': return 'primary';
      case 'DISCARDED': return 'default';
      default: return 'default';
    }
  };

  const filteredClients = clients.filter(client =>
    client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.lastName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && !selectedClient) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs />
      
      {/* Header */}
      <Box sx={{
        mb: 3,
        mt: { xs: '50px', md: 0 },
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: 3,
        borderRadius: 2,
        color: 'white',
        pl: { xs: '16px', md: 3 },
        transition: 'margin 0.2s ease',
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
          Client Analytics & Inventory Tracking
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
          View client orders, inventory usage, and track vial activity
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Client Selector - Prominent at top */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Select a Client to View Analytics
        </Typography>
        <Autocomplete
          options={sortedClients || []}
          value={selectedClient}
          onChange={handleClientSelect}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option ? `${option.firstName} ${option.lastName} - ${option.email}` : ''}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Avatar sx={{ mr: 2, bgcolor: 'primary.main', width: 32, height: 32 }}>
                {option.firstName?.[0]}{option.lastName?.[0]}
              </Avatar>
              <Box>
                <Typography variant="body1">
                  {option.firstName} {option.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.email}
                </Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search for a client by name or email"
              placeholder="Start typing to search..."
              variant="outlined"
              helperText="Select a client to view their orders and inventory tracking data"
            />
          )}
          loading={loading && !selectedClient}
          noOptionsText="No clients found"
          fullWidth
        />
      </Paper>

      {/* Main Content Area */}
      {!selectedClient ? (
        <>
          {/* Overall Dashboard - Show when no client selected */}
          <Grid container spacing={3}>
            {/* Overall KPI Cards */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PeopleIcon sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Total Clients
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight="bold">
                    {overallStats.totalClients}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active users
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <OrdersIcon sx={{ color: 'warning.main', mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Pending Orders
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight="bold" color="warning.main">
                    {overallStats.pendingOrders}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Awaiting processing
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <InventoryIcon sx={{ color: 'success.main', mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Tracked Inventory
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight="bold">
                    {overallStats.totalInventoryTracked}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total vials tracked
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrendingUpIcon sx={{ color: 'info.main', mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Total Revenue
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight="bold">
                    {formatCurrency(overallStats.totalRevenue)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last 20 orders
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Orders */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Recent Orders Across All Clients
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => navigate('/internal/order-processing')}
                  >
                    View All Orders
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order #</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Order Date</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {overallOrders.slice(0, 10).map((order) => (
                        <TableRow key={order.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {order.orderNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {order.user ? `${order.user.firstName} ${order.user.lastName}` : 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatDate(order.orderDate)}</TableCell>
                          <TableCell>{formatCurrency(order.totalAmount)}</TableCell>
                          <TableCell>
                            <Chip
                              label={order.status}
                              size="small"
                              color={
                                order.status === 'SHIPPED' ? 'success' :
                                order.status === 'PENDING' ? 'warning' :
                                order.status === 'PROCESSING' ? 'info' : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              onClick={() => {
                                const client = clients.find(c => c.id === order.userId);
                                if (client) {
                                  handleClientSelect(null, client);
                                } else {
                                  // If client not in list (maybe pagination?), create minimal client obj from order data
                                  if (order.user) {
                                    const minimalClient = {
                                      id: order.userId,
                                      firstName: order.user.firstName,
                                      lastName: order.user.lastName,
                                      email: order.user.email,
                                      organization: org
                                    };
                                    handleClientSelect(null, minimalClient);
                                  }
                                }
                              }}
                            >
                              View Client
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {overallOrders.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No recent orders
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Quick Stats */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  System Activity
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
                    <Typography variant="body2">Active Orders Processing</Typography>
                    <Chip label={overallStats.activeOrders} color="info" size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
                    <Typography variant="body2">Orders Awaiting Action</Typography>
                    <Chip label={overallStats.pendingOrders} color="warning" size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Total Vials Being Tracked</Typography>
                    <Chip label={overallStats.totalInventoryTracked} color="success" size="small" />
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<OrdersIcon />}
                    onClick={() => navigate('/internal/order-processing')}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Process Orders
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<InventoryIcon />}
                    onClick={() => navigate('/internal/inventory-management')}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Manage Inventory
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<PeopleIcon />}
                    onClick={() => navigate('/internal/client-management')}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Manage Clients
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      ) : (
        <>
          {/* Client Header Card */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: '2rem' }}>
                  {selectedClient.firstName?.[0]}{selectedClient.lastName?.[0]}
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h4" fontWeight="bold">
                  {selectedClient.firstName} {selectedClient.lastName}
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  {selectedClient.email}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip
                    label={selectedClient.organization || 'EPL'}
                    size="small"
                    color="primary"
                  />
                  <Chip
                    label="Active Client"
                    size="small"
                    color="success"
                  />
                </Box>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon />}
                  onClick={() => console.log('Export client data')}
                  size="large"
                >
                  Export Report
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab 
                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>Overview</Typography>
                </Box>} 
              />
              <Tab 
                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InventoryIcon fontSize="small" />
                  <Typography>Inventory Tracking</Typography>
                  <Chip label={inventoryStats.total} size="small" color="primary" />
                </Box>} 
              />
              <Tab 
                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <OrdersIcon fontSize="small" />
                  <Typography>Order History</Typography>
                  <Chip label={orderStats.total} size="small" />
                </Box>} 
              />
              <Tab 
                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon fontSize="small" />
                  <Typography>Usage Analytics</Typography>
                </Box>} 
              />
            </Tabs>
          </Paper>

              {/* Tab Content */}
              {activeTab === 0 && (
                <>
                  {/* Overview - KPI Cards */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <OrdersIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              Total Orders
                            </Typography>
                          </Box>
                          <Typography variant="h4" fontWeight="bold">
                            {orderStats.total}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(orderStats.totalValue)} total value
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <InventoryIcon sx={{ color: 'success.main', mr: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              Total Inventory
                            </Typography>
                          </Box>
                          <Typography variant="h4" fontWeight="bold">
                            {inventoryStats.total}
                          </Typography>
                          <Typography variant="caption" color="success.main">
                            {inventoryStats.unused} unused vials
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <UsedIcon sx={{ color: 'info.main', mr: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              Vials Used
                            </Typography>
                          </Box>
                          <Typography variant="h4" fontWeight="bold">
                            {inventoryStats.used}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {inventoryStats.total > 0 ? Math.round((inventoryStats.used / inventoryStats.total) * 100) : 0}% usage rate
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <WarningIcon sx={{ color: 'warning.main', mr: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              Expiring Soon
                            </Typography>
                          </Box>
                          <Typography variant="h4" fontWeight="bold">
                            {inventoryStats.expiringWithin30Days}
                          </Typography>
                          <Typography variant="caption" color="warning.main">
                            Within 30 days
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Recent Activity */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Recent Orders
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {clientOrders.slice(0, 5).map((order) => (
                          <Box key={order.id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {order.orderNumber}
                              </Typography>
                              <Chip
                                label={order.status}
                                size="small"
                                color={order.status === 'SHIPPED' ? 'success' : 'default'}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(order.orderDate)} • {formatCurrency(order.totalAmount)}
                            </Typography>
                          </Box>
                        ))}
                        {clientOrders.length === 0 && (
                          <Typography variant="body2" color="text.secondary" align="center">
                            No orders found
                          </Typography>
                        )}
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Inventory Status
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Unused</Typography>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {inventoryStats.unused}
                            </Typography>
                          </Box>
                          <Box sx={{ height: 8, bgcolor: '#eee', borderRadius: 1, overflow: 'hidden' }}>
                            <Box
                              sx={{
                                height: '100%',
                                width: `${inventoryStats.total > 0 ? (inventoryStats.unused / inventoryStats.total) * 100 : 0}%`,
                                bgcolor: 'success.main'
                              }}
                            />
                          </Box>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Used</Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary.main">
                              {inventoryStats.used}
                            </Typography>
                          </Box>
                          <Box sx={{ height: 8, bgcolor: '#eee', borderRadius: 1, overflow: 'hidden' }}>
                            <Box
                              sx={{
                                height: '100%',
                                width: `${inventoryStats.total > 0 ? (inventoryStats.used / inventoryStats.total) * 100 : 0}%`,
                                bgcolor: 'primary.main'
                              }}
                            />
                          </Box>
                        </Box>

                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Discarded</Typography>
                            <Typography variant="body2" fontWeight="bold" color="text.secondary">
                              {inventoryStats.discarded}
                            </Typography>
                          </Box>
                          <Box sx={{ height: 8, bgcolor: '#eee', borderRadius: 1, overflow: 'hidden' }}>
                            <Box
                              sx={{
                                height: '100%',
                                width: `${inventoryStats.total > 0 ? (inventoryStats.discarded / inventoryStats.total) * 100 : 0}%`,
                                bgcolor: 'text.secondary'
                              }}
                            />
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </>
              )}

              {activeTab === 1 && (
                <>
                  {/* Inventory Tracking Tab */}
                  <Paper>
                    <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={statusFilter}
                          label="Status"
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <MenuItem value="ALL">All Status</MenuItem>
                          <MenuItem value="UNUSED">Unused</MenuItem>
                          <MenuItem value="USED">Used</MenuItem>
                          <MenuItem value="DISCARDED">Discarded</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Tissue ID</TableCell>
                            <TableCell>Order #</TableCell>
                            <TableCell>Product</TableCell>
                            <TableCell>Order Date</TableCell>
                            <TableCell>Shipped Date</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loading ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                <CircularProgress />
                              </TableCell>
                            </TableRow>
                          ) : clientInventory.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                <Typography color="text.secondary">
                                  No inventory items found
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            clientInventory
                              .filter(item => statusFilter === 'ALL' || item.status === statusFilter)
                              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                              .map((item) => (
                                <TableRow key={item.id} hover>
                                  <TableCell>
                                    <Typography variant="body2" fontFamily="monospace">
                                      {item.tissueId}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{item.orderNumber}</TableCell>
                                  <TableCell>{item.productName}</TableCell>
                                  <TableCell>{formatDate(item.orderDate)}</TableCell>
                                  <TableCell>{formatDate(item.shippedDate)}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={item.status}
                                      size="small"
                                      color={getStatusColor(item.status)}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Tooltip title="View Details">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleViewDetails(item)}
                                      >
                                        <ViewIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={clientInventory.filter(item => statusFilter === 'ALL' || item.status === statusFilter).length}
                      page={page}
                      onPageChange={(e, newPage) => setPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                      }}
                    />
                  </Paper>
                </>
              )}

              {activeTab === 2 && (
                <>
                  {/* Order History Tab */}
                  <Paper>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Order #</TableCell>
                            <TableCell>Order Date</TableCell>
                            <TableCell>Items</TableCell>
                            <TableCell>Total Amount</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Shipped Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {clientOrders.map((order) => (
                            <TableRow key={order.id} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {order.orderNumber}
                                </Typography>
                              </TableCell>
                              <TableCell>{formatDate(order.orderDate)}</TableCell>
                              <TableCell>{order.items?.length || 0}</TableCell>
                              <TableCell>{formatCurrency(order.totalAmount)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={order.status}
                                  size="small"
                                  color={order.status === 'SHIPPED' ? 'success' : 'default'}
                                />
                              </TableCell>
                              <TableCell>{formatDate(order.shippedAt)}</TableCell>
                            </TableRow>
                          ))}
                          {clientOrders.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                <Typography color="text.secondary">
                                  No orders found
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </>
              )}

              {activeTab === 3 && (
                <>
                  {/* Usage Analytics Tab */}
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Usage Analytics
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Average Time to Usage
                            </Typography>
                            <Typography variant="h4" fontWeight="bold">
                              --
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Days from shipment to usage
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Discard Rate
                            </Typography>
                            <Typography variant="h4" fontWeight="bold">
                              {inventoryStats.total > 0 
                                ? Math.round((inventoryStats.discarded / inventoryStats.total) * 100) 
                                : 0}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {inventoryStats.discarded} of {inventoryStats.total} vials
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12}>
                        <Alert severity="info">
                          <Typography variant="body2">
                            <strong>Note:</strong> Detailed usage analytics will populate as the client logs more usage data.
                            This includes injection sites, horse names, and usage patterns over time.
                          </Typography>
                        </Alert>
                      </Grid>
                    </Grid>
                  </Paper>
                </>
              )}
            </>
          )}

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Inventory Item Details</DialogTitle>
        <DialogContent>
          {selectedInventoryItem && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Tissue ID
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {selectedInventoryItem.tissueId}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box>
                    <Chip
                      label={selectedInventoryItem.status}
                      size="small"
                      color={getStatusColor(selectedInventoryItem.status)}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Order Number
                  </Typography>
                  <Typography variant="body2">
                    {selectedInventoryItem.orderNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Product
                  </Typography>
                  <Typography variant="body2">
                    {selectedInventoryItem.productName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Order Date
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedInventoryItem.orderDate)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Shipped Date
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedInventoryItem.shippedDate)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientAnalytics;
